/**
 * product-form.js — Urdile Shopify Theme
 * VariantPicker y QuantityInput custom elements
 */

'use strict';

// ============================================================
// VariantPicker — selector de variantes de producto
// ============================================================
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this._currentVariantId = null;
    this._variantData = this._getVariantDataFromDOM();
    this._bindEvents();
    // Inicializar con la variante activa actual
    this.onVariantChange();
  }

  _bindEvents() {
    // Escucha cambios en selects y botones de variante
    this.querySelectorAll('select[name]').forEach((select) => {
      select.addEventListener('change', this.onVariantChange.bind(this));
    });

    this.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', this.onVariantChange.bind(this));
    });

    // Botones de variante (para swatches o botones de talla)
    this.querySelectorAll('.product__variant-btn').forEach((btn) => {
      btn.addEventListener('click', this._onVariantBtnClick.bind(this));
    });
  }

  _onVariantBtnClick(evt) {
    const btn = evt.currentTarget;
    const group = btn.closest('.product__variant-buttons');
    if (!group) return;

    // Deseleccionar botones del mismo grupo
    group.querySelectorAll('.product__variant-btn').forEach((b) => {
      b.classList.remove('product__variant-btn--selected');
      b.setAttribute('aria-pressed', 'false');
    });

    btn.classList.add('product__variant-btn--selected');
    btn.setAttribute('aria-pressed', 'true');

    this.onVariantChange();
  }

  onVariantChange() {
    this.updateOptions();
    const variant = this.getVariantData();
    this.updateMasterId(variant);
    this.updateMedia(variant);
    this.updateURL(variant);
    this.updateVariantInput(variant);
    this.renderProductInfo(variant);
  }

  updateOptions() {
    this._selectedOptions = [];

    // De selects
    this.querySelectorAll('select[name]').forEach((select) => {
      this._selectedOptions.push(select.value);
    });

    // De radio buttons
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) this._selectedOptions.push(checked.value);
    });

    // De botones seleccionados
    this.querySelectorAll('.product__variant-buttons').forEach((group) => {
      const selected = group.querySelector('.product__variant-btn--selected');
      if (selected) this._selectedOptions.push(selected.dataset.value || selected.textContent.trim());
    });
  }

  getVariantData() {
    if (!this._variantData || !this._selectedOptions) return null;

    return this._variantData.find((variant) => {
      return this._selectedOptions.every((option, index) => {
        return variant[`option${index + 1}`] === option;
      });
    }) || null;
  }

  updateMasterId(variant) {
    this._currentVariantId = variant ? variant.id : null;

    const productSelect = document.getElementById('ProductSelect');
    if (productSelect) {
      productSelect.value = this._currentVariantId || '';
    }
  }

  updateSelectedSwatchValue(event) {
    const input = event.target;
    const label = input.closest('label');
    if (label) {
      const group = input.closest('.product__variant-group');
      const valueDisplay = group && group.querySelector('.product__variant-value');
      if (valueDisplay) valueDisplay.textContent = input.value;
    }
  }

  updateMedia(variant) {
    if (!variant || !variant.featured_media) return;

    // Despacha evento personalizado para que la galería lo maneje
    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant, mediaId: variant.featured_media.id },
      })
    );
  }

  updateURL(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  }

  updateVariantInput(variant) {
    const forms = document.querySelectorAll(`#product-form-${this.dataset.productId}, form[data-product-id="${this.dataset.productId}"]`);
    forms.forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  renderProductInfo(variant) {
    if (!variant) {
      this._setAddToCartState('unavailable');
      return;
    }

    // Actualizar precio
    const priceEl = document.querySelector('.product__price');
    if (priceEl && typeof formatMoney === 'function') {
      priceEl.textContent = formatMoney(variant.price);
    }

    // Precio tachado (comparación)
    const compareEl = document.querySelector('.product__price-compare');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    // Badge de descuento
    const savingsEl = document.querySelector('.product__savings');
    if (savingsEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        savingsEl.textContent = `-${pct}%`;
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }

    // Estado del botón agregar
    if (!variant.available) {
      this._setAddToCartState('soldout');
    } else {
      this._setAddToCartState('available');
    }
  }

  _setAddToCartState(state) {
    const btn = document.querySelector('.product__add-to-cart');
    if (!btn) return;

    const texts = {
      available: 'Agregar al carro',
      soldout: 'Sin stock',
      unavailable: 'No disponible',
    };

    btn.textContent = texts[state] || texts.unavailable;
    btn.disabled = state !== 'available';
  }

  _getVariantDataFromDOM() {
    const scriptEl = this.querySelector('script[type="application/json"]');
    if (!scriptEl) return [];
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (e) {
      console.error('VariantPicker: error parseando variant data', e);
      return [];
    }
  }
}

customElements.define('variant-picker', VariantPicker);

// ============================================================
// QuantityInput — stepper de cantidad + y -
// ============================================================
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('.quantity-input__field');
    if (!this._input) return;

    this.querySelectorAll('.quantity-input__btn').forEach((btn) => {
      btn.addEventListener('click', this._onBtnClick.bind(this));
    });

    this._input.addEventListener('change', this._onInputChange.bind(this));
    this._input.addEventListener('blur', this._onInputBlur.bind(this));
  }

  _onBtnClick(evt) {
    const btn = evt.currentTarget;
    const action = btn.dataset.action;
    let value = parseInt(this._input.value, 10) || 1;

    if (action === 'increase') {
      value = value + 1;
    } else if (action === 'decrease') {
      value = Math.max(1, value - 1);
    }

    this._input.value = value;
    this._input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _onInputChange() {
    let value = parseInt(this._input.value, 10);
    if (isNaN(value) || value < 1) {
      this._input.value = 1;
    }
  }

  _onInputBlur() {
    this._onInputChange();
  }
}

customElements.define('quantity-input', QuantityInput);


// ============================================================
// PDP fixes (Fase QA · iter 1)
// - Qty stepper con event delegation (sin requerir <quantity-input>)
// - Form submit con AJAX + cart drawer (sin navegación)
// ============================================================
(function() {
  'use strict';

  // Qty stepper: delegated click handler que lee data-qty-action
  document.addEventListener('click', function(evt) {
    var btn = evt.target.closest('[data-qty-action]');
    if (!btn) return;
    var wrapper = btn.closest('.quantity-input');
    if (!wrapper) return;
    var input = wrapper.querySelector('.quantity-input__field, input[type="number"]');
    if (!input) return;
    var action = btn.dataset.qtyAction;
    var val = parseInt(input.value, 10) || 1;
    var max = parseInt(input.max, 10) || Infinity;
    if (action === 'increase' && val < max) val++;
    else if (action === 'decrease' && val > 1) val--;
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Form submit: intercept y usar Section Rendering API
  document.addEventListener('submit', async function(evt) {
    var form = evt.target.closest('form[data-pdp-form]');
    if (!form) return;
    evt.preventDefault();

    var btn = form.querySelector('[data-pdp-add]');
    var btnText = btn ? (btn.querySelector('span') || btn) : null;
    var originalText = btnText ? btnText.textContent : null;
    if (btn) {
      btn.disabled = true;
      if (btnText) btnText.textContent = 'Agregando...';
    }

    try {
      if (typeof CartAPI === 'undefined') {
        // Fallback: submit normal
        form.submit();
        return;
      }
      var formData = new FormData(form);
      var result = await CartAPI.add(formData);
      var cartState = await CartAPI.get();
      if (typeof updateCartCount === 'function') updateCartCount(cartState.item_count);
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        if (result.sections && result.sections['cart-drawer']) cartDrawer._renderFromSection(result);
        cartDrawer.open(btn);
      }
      if (window.UrdileToast) window.UrdileToast.success('✓ Producto agregado al carro');
    } catch (err) {
      console.error('PDP add error:', err);
      if (window.UrdileToast) window.UrdileToast.danger('No se pudo agregar el producto: ' + (err.message || ''));
    } finally {
      if (btn) {
        btn.disabled = false;
        if (btnText && originalText) btnText.textContent = originalText;
      }
    }
  });
})();


// ============================================================
// PDP tabs (Descripción / Specs / Reseñas)
// ============================================================
(function() {
  document.addEventListener('click', function(evt) {
    var btn = evt.target.closest('.pdp-tabs__tab[data-tab-target]');
    if (!btn) return;
    evt.preventDefault();
    var target = btn.dataset.tabTarget;
    var nav = btn.closest('.pdp-tabs__nav');
    if (!nav) return;
    var panels = nav.parentElement.querySelector('.pdp-tabs__panels');

    // Desactivar todos
    nav.querySelectorAll('.pdp-tabs__tab').forEach(function(t) {
      t.classList.remove('pdp-tabs__tab--active');
      t.setAttribute('aria-selected', 'false');
    });
    if (panels) {
      panels.querySelectorAll('.pdp-tabs__panel').forEach(function(p) {
        p.classList.remove('pdp-tabs__panel--active');
        p.setAttribute('hidden', '');
      });
    }

    // Activar el seleccionado
    btn.classList.add('pdp-tabs__tab--active');
    btn.setAttribute('aria-selected', 'true');
    var panel = panels && panels.querySelector('[data-tab-panel="' + target + '"]');
    if (panel) {
      panel.classList.add('pdp-tabs__panel--active');
      panel.removeAttribute('hidden');
    }
  });
})();

// ============================================================
// PDP gallery — thumb click cambia el slide activo
// ============================================================
(function() {
  document.addEventListener('click', function(evt) {
    var thumb = evt.target.closest('.pdp-gallery__thumb[data-thumb-index]');
    if (!thumb) return;
    evt.preventDefault();
    var idx = thumb.dataset.thumbIndex;
    var gallery = thumb.closest('.pdp-gallery');
    if (!gallery) return;

    // Update thumbs aria-selected + active class
    gallery.querySelectorAll('.pdp-gallery__thumb').forEach(function(t) {
      t.classList.remove('pdp-gallery__thumb--active');
      t.setAttribute('aria-selected', 'false');
    });
    thumb.classList.add('pdp-gallery__thumb--active');
    thumb.setAttribute('aria-selected', 'true');

    // Update slides
    gallery.querySelectorAll('.pdp-gallery__slide').forEach(function(s) {
      if (s.dataset.slideIndex === idx) s.classList.add('pdp-gallery__slide--active');
      else s.classList.remove('pdp-gallery__slide--active');
    });
  });
})();


// ============================================================
// PDP share button — Web Share API o copy URL fallback
// ============================================================
(function() {
  document.addEventListener('click', async function(evt) {
    var btn = evt.target.closest('[data-share-toggle]');
    if (!btn) return;
    evt.preventDefault();
    var url = btn.dataset.shareUrl || window.location.href;
    var title = btn.dataset.shareTitle || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ title: title, url: url });
      } catch(e) { /* user canceled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        if (window.UrdileToast) window.UrdileToast.success('Link copiado al portapapeles');
      } catch(e) {
        if (window.UrdileToast) window.UrdileToast.danger('No se pudo copiar el link');
      }
    }
  });
})();


// ============================================================
// PDP zoom on hover — cursor-following zoom
// ============================================================
(function() {
  function setup(slide) {
    if (slide.dataset.zoomReady) return;
    slide.dataset.zoomReady = '1';

    slide.addEventListener('mouseenter', function() {
      slide.classList.add('pdp-gallery__slide--zoom');
    });
    slide.addEventListener('mouseleave', function() {
      slide.classList.remove('pdp-gallery__slide--zoom');
      var img = slide.querySelector('.pdp-gallery__img');
      if (img) img.style.transformOrigin = 'center center';
    });
    slide.addEventListener('mousemove', function(e) {
      var img = slide.querySelector('.pdp-gallery__img');
      if (!img) return;
      var rect = slide.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
    });
  }

  function init() {
    document.querySelectorAll('.pdp-gallery__slide').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
